/**
 * Node customizado para perguntas (botões e lista)
 * Design com múltiplas saídas para cada opção + handle de resposta inválida
 * Inclui toolbar flutuante ao selecionar
 * Usa refs para calcular posições dinâmicas dos handles
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { MessageCircleQuestion, List, AlertTriangle } from 'lucide-react';
import { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { FlowNodeData } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Calcula o offset vertical de um elemento relativo a um container ancestral
 * Percorre a cadeia de offsetParent para obter posição precisa
 * independente de transforms CSS ou timing de renderização
 */
function getOffsetRelativeTo(element: HTMLElement, container: HTMLElement): number {
  let offset = 0;
  let current: HTMLElement | null = element;
  while (current && current !== container) {
    offset += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }
  return offset;
}

/**
 * Componente visual do node de pergunta
 * Cada opção tem seu próprio handle de saída para ramificação
 * Handles desconectados aparecem em vermelho como alerta visual
 * Handle de "Resposta Inválida" aparece quando configurado
 * Posições dos handles são calculadas dinamicamente via refs
 */
export function QuestionNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData & { edges?: any[] };
  const isList = nodeData.type === 'question_list';
  const Icon = isList ? List : MessageCircleQuestion;
  
  // Refs para calcular posições dinâmicas dos handles
  const containerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const invalidResponseRef = useRef<HTMLDivElement>(null);
  
  // Estado para armazenar posições calculadas dos handles
  const [handlePositions, setHandlePositions] = useState<number[]>([]);
  const [invalidHandlePosition, setInvalidHandlePosition] = useState<number>(0);
  // Trigger para forçar recálculo via ResizeObserver
  const [recalcTrigger, setRecalcTrigger] = useState(0);
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  const question = nodeData.config?.text || 'Pergunta não configurada';
  const options = nodeData.config?.options || [];
  const preview = question.length > 60 ? question.substring(0, 60) + '...' : question;
  const edges = nodeData.edges || [];
  
  // Verifica se resposta inválida está configurada para goto_node
  const invalidResponse = nodeData.config?.invalid_response;
  const hasInvalidResponseHandle = invalidResponse?.enabled && 
    (invalidResponse?.action === 'goto_node' || 
     invalidResponse?.exceeded_action === 'goto_node');

  /**
   * ResizeObserver para recalcular posições quando o layout muda
   * Garante recálculo após montagem tardia no funil tempo real
   */
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      setRecalcTrigger(prev => prev + 1);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  /**
   * Calcula posições dos handles usando offsetTop/offsetHeight
   * Usa offsets DOM ao invés de getBoundingClientRect para evitar
   * problemas com transforms do React Flow e timing de renderização
   */
  useLayoutEffect(() => {
    // Guard 1: Container deve existir
    if (!containerRef.current) return;
    
    // Guard 2: Se não há opções, limpa posições e sai
    if (options.length === 0) {
      setHandlePositions(prev => prev.length > 0 ? [] : prev);
      setInvalidHandlePosition(prev => prev !== 0 ? 0 : prev);
      return;
    }
    
    // Guard 3: Aguarda refs das opções serem populadas
    const populatedRefs = optionRefs.current.filter(Boolean);
    if (populatedRefs.length === 0) return;
    
    // Guard 4: Verifica se container tem dimensões válidas
    if (containerRef.current.offsetWidth === 0 || containerRef.current.offsetHeight === 0) return;
    
    // Calcula posição central de cada opção usando offsets DOM
    const container = containerRef.current;
    const newPositions = optionRefs.current.map(ref => {
      if (!ref) return 0;
      const offsetTop = getOffsetRelativeTo(ref, container);
      return offsetTop + (ref.offsetHeight / 2);
    });
    
    // Só atualiza estado se as posições realmente mudaram (evita loop infinito)
    setHandlePositions(prev => {
      const changed = newPositions.length !== prev.length ||
        newPositions.some((pos, i) => Math.abs(pos - (prev[i] || 0)) > 0.5);
      return changed ? newPositions : prev;
    });
    
    // Posição do handle de resposta inválida
    if (invalidResponseRef.current && hasInvalidResponseHandle) {
      const offsetTop = getOffsetRelativeTo(invalidResponseRef.current, container);
      const newInvalidPos = offsetTop + (invalidResponseRef.current.offsetHeight / 2);
      setInvalidHandlePosition(prev => Math.abs(newInvalidPos - prev) > 0.5 ? newInvalidPos : prev);
    }
  }, [options, hasInvalidResponseHandle, recalcTrigger]);

  /**
   * Verifica se um handle específico tem conexão
   */
  const hasConnection = (handleId: string): boolean => {
    return edges.some(
      (edge: any) => edge.source === id && edge.sourceHandle === handleId
    );
  };

  return (
    <div ref={containerRef} className="relative bg-card rounded-lg shadow-lg border-2 border-blue-500 min-w-[220px] max-w-[320px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-blue-500 !w-3 !h-3"
      />
      
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-blue-500/10 text-blue-600 p-2 rounded">
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-foreground">
              {nodeData.config?.nickname || (isList ? 'Lista de Opções' : 'Pergunta com Botões')}
            </div>
          </div>
        </div>
        
        <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 mb-2">
          {preview}
        </div>
        
        {/* Exibe todas as opções com refs para medição */}
        {options.length > 0 && (
          <div className="space-y-1">
            {options.map((option: any, index: number) => (
              <div 
                key={index} 
                ref={el => optionRefs.current[index] = el}
                className="text-xs bg-blue-500/5 text-blue-700 rounded px-2 py-1 border border-blue-200 relative"
              >
                {option.label || option.title}
              </div>
            ))}
          </div>
        )}

        {/* Indicador de handle de resposta inválida com ref */}
        {hasInvalidResponseHandle && (
          <div 
            ref={invalidResponseRef}
            className="mt-2 text-xs bg-amber-500/10 text-amber-700 rounded px-2 py-1 border border-amber-300 flex items-center gap-1"
          >
            <AlertTriangle className="h-3 w-3" />
            Resposta Inválida
          </div>
        )}
      </div>
      
      {/* Handles dinâmicos posicionados via cálculo de refs */}
      {options.length > 0 && handlePositions.length > 0 ? (
        options.map((option: any, index: number) => {
          const handleId = `option_${index}`;
          const isConnected = hasConnection(handleId);
          
          return (
            <Handle
              key={`option-${index}`}
              type="source"
              position={Position.Right}
              id={handleId}
              className={isConnected ? "!bg-blue-500 !w-3 !h-3" : "!bg-red-500 !w-3 !h-3 animate-pulse"}
              style={{ top: `${handlePositions[index] || 0}px` }}
              title={isConnected ? 'Conectado' : `Sem conexão: ${option.label || option.title}`}
            />
          );
        })
      ) : options.length === 0 ? (
        // Handle default quando não há opções configuradas
        <Handle
          type="source"
          position={Position.Bottom}
          className="!bg-blue-500 !w-3 !h-3"
        />
      ) : null}

      {/* Handle de resposta inválida (quando configurado para goto_node) */}
      {hasInvalidResponseHandle && invalidHandlePosition > 0 && (
        <Handle
          type="source"
          position={Position.Right}
          id="invalid_response"
          className={hasConnection('invalid_response') ? "!bg-amber-500 !w-3 !h-3" : "!bg-amber-500 !w-3 !h-3 animate-pulse"}
          style={{ top: `${invalidHandlePosition}px` }}
          title={hasConnection('invalid_response') ? 'Conectado: Resposta Inválida' : 'Sem conexão: Resposta Inválida'}
        />
      )}
    </div>
  );
}
